import React from 'react';
import useTripStore from '../../store/useTripStore';
import { useSelectedType } from '../../hooks/useTripStoreSelectors';
import { useHotelOwnership } from '../../hooks/useHotelOwnership';
import { useHotelPriceOverride } from '../../hooks/useHotelPriceOverride';
import { useTripDayHotelData } from './useTripDayHotelData';
import { useTripDayHotelSelection } from './useTripDayHotelSelection';
import TripDayHotelEmptyState from './TripDayHotelEmptyState';
import TripDayHotelUnavailable from './TripDayHotelUnavailable';
import TripDayHotelCard from './TripDayHotelCard';

export interface TripDayHotelProps {
  dayIndex: number;
  addHotel: (hotel: any) => void;
  date?: any;
  // KD-3643 — handed down by the trip so the stay can say whether it is
  // bookable; the day itself has no use for it. TO-only today.
  registerHotelWarning?: (key: any, status: any) => void;
  // Whether the current app may edit this day at all (per useIsDayEditable,
  // DMC only — TO's days are always editable). A hotel may still be removed
  // on a read-only day when the app owns the hotel itself, per
  // useHotelOwnership — the two are independent gates.
  readOnly?: boolean;
  locale?: string;
  translation: (key: string, options?: Record<string, any>) => string;
  // Precomputed labels/price slot for TripHotelCard (the suggested-hotel
  // modal's card), the app's own i18n bundled once per call site.
  tripHotelCardProps?: any;
  onEvent?: (name: string, payload: unknown) => void;
  // TO-only (KD-3643): renders the corner "cannot be booked" tooltip. Absent
  // for an app that has no such warning to show.
  renderAccessAlert?: (info: { tooltip: string }) => React.ReactNode;
  // The non-included hotel's own room-selection modal is still a large,
  // per-app component (not moved by this PRD) — rendered through this slot
  // with every piece of data it needs already computed.
  renderRoomSelectionModal: (props: {
    isOpen: boolean;
    onRequestClose: () => void;
    onSave: (rooms: any[]) => void;
    hotelData: { possibilities: any[] };
    totalRoomNumber: number;
    initialSelectedRooms: any[];
    hotelName: any;
    filters: { rooms: any[]; startAt: any; endAt: any };
  }) => React.ReactNode;
}

export default function TripDayHotel({
  dayIndex,
  addHotel,
  date,
  registerHotelWarning,
  readOnly = false,
  locale,
  translation: t,
  tripHotelCardProps,
  onEvent,
  renderAccessAlert,
  renderRoomSelectionModal,
}: TripDayHotelProps) {
  const updateDay = useTripStore((state) => state.updateDay);
  const setHotelInStore = useTripStore((state) => state.setHotel);
  const selectedType = useSelectedType();

  const data = useTripDayHotelData(dayIndex);
  const {
    day,
    hotel,
    hotelIsNotDistributed,
    isFirstInGroup,
    hotelGroup,
    program,
    suggestedHotelIds,
  } = data;

  const { canMutateHotel, canRemoveHotel } = useHotelOwnership(dayIndex, hotel);
  const hotelPriceOverride = useHotelPriceOverride(hotel);

  const selection = useTripDayHotelSelection({
    dayIndex,
    day,
    hotel,
    hotelGroup,
    hotelIsNotDistributed,
    days: data.days,
    program,
    roomsComposition: data.roomsComposition,
    isFirstInGroup,
    isIncludedHotel: data.isIncludedHotel,
    roomsData: data.roomsData,
    roomsError: data.roomsError,
    roomStartDate: data.roomStartDate,
    roomEndDate: data.roomEndDate,
    isGroupLoading: data.isGroupLoading,
    hasGroupError: data.hasGroupError,
    isMinStayUnmet: data.isMinStayUnmet,
    dayRoomSelection: data.dayRoomSelection,
    registerHotelWarning,
    hotelPriceOverride,
  });

  const setHotel = (value: any) => {
    if (value?.id) {
      // Setting a hotel
      updateDay(dayIndex, {
        hotelId: value.id,
      });
      setHotelInStore(value.id, value);
    } else {
      // Removing hotel - clear all hotel-related data
      updateDay(dayIndex, {
        hotelId: null,
        hotelPricing: null,
        selectedRoom: null,
        hotelSelectedRoomKey: null,
        roomsComposition: null,
        hotelNights: undefined,
        hotelRoomCount: undefined,
        previouslySelectedRoomName: null,
        previouslySelectedRoomNames: null,
        hotelBookedOnMyOwn: false,
      });
    }
  };

  const removeHotel = () => {
    onEvent?.('hotel.removed', {
      hotelId: hotel?.id,
      hotelName: hotel?.name ?? hotel?.displayName,
      dayIndex,
    });

    // If removing the first day of a multi-day group, transfer room selection to the next day
    if (isFirstInGroup && hotelGroup?.dayIndices?.length > 1) {
      const nextDayIndex = hotelGroup.dayIndices[1];

      updateDay(nextDayIndex, {
        selectedRoom: day?.selectedRoom ?? null,
        hotelPricing: day?.hotelPricing ?? null,
        hotelBookedOnMyOwn: day?.hotelBookedOnMyOwn ?? false,
      });
    }

    setHotel(null);
  };

  if (!hotel) {
    return (
      <TripDayHotelEmptyState
        dayIndex={dayIndex}
        date={date}
        program={program}
        suggestedHotelIds={suggestedHotelIds}
        addHotel={addHotel}
        readOnly={readOnly}
        locale={locale}
        tripHotelCardProps={tripHotelCardProps}
        t={t}
      />
    );
  }

  if (hotelIsNotDistributed) {
    return (
      <TripDayHotelUnavailable
        hotel={hotel}
        isIncludedHotel={data.isIncludedHotel}
        onRemove={() => removeHotel()}
        t={t}
      />
    );
  }

  return (
    <TripDayHotelCard
      dayIndex={dayIndex}
      date={date}
      addHotel={addHotel}
      selectedType={selectedType}
      data={data}
      selection={selection}
      removeHotel={removeHotel}
      readOnly={readOnly}
      canMutateHotel={canMutateHotel}
      canRemoveHotel={canRemoveHotel}
      t={t}
      locale={locale}
      tripHotelCardProps={tripHotelCardProps}
      renderAccessAlert={renderAccessAlert}
      renderRoomSelectionModal={renderRoomSelectionModal}
    />
  );
}
